import { forwardRef } from 'react';
import {
  Accordion,
  ActionIcon,
  Divider,
  Group,
  List,
  ScrollArea,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useFocusTrap } from '@mantine/hooks';
import { LOGIC_CATEGORIES, LogicBlockIndex } from '@morph-mapper/node-logic';
import { getKeys } from '@morph-mapper/utils';
import { useNotify } from '@/hooks';
import { useGraphContext } from '../../context';
import { useCommand } from './use-command';
import { useQueryLogicBlocks } from './use-query-logic-blocks';
import { t } from 'i18next';
import { IoCloseSharp } from 'react-icons/io5';
import { BsSearch } from 'react-icons/bs';
import {
  DrawerStack,
  DrawerRoot,
  StyledAccordion,
  StyledComponent,
  StyledList,
  StyledThemeIcon,
} from './styles';
import { LogicBlockView } from './types';

export const NodeSelectDrawer = () => {
  /** Context state */
  const closeDrawer = useGraphContext(({ ui }) => ui.closeDrawer);
  /** Hooks */
  const focusTrapRef = useFocusTrap();
  const { execute } = useCommand();
  const { error } = useNotify();
  const { categories, query, setQuery } = useQueryLogicBlocks();

  const handleSelect = (type: LogicBlockIndex) => {
    const result = execute(type);

    if (result.isErr()) {
      error(result.error.message);
      return;
    }

    closeDrawer();
  };

  return (
    <DrawerRoot>
      {/** Header */}
      <NodeSelectDrawerHeader
        ref={focusTrapRef}
        query={query}
        onQueryChange={setQuery}
      />
      <Divider />
      {/** Body */}
      <ScrollArea style={{ flex: 1 }} scrollbarSize={5}>
        <DrawerStack>
          {categories.map(({ category, blocks }) => (
            <StyledAccordion
              multiple
              defaultValue={getKeys(LOGIC_CATEGORIES)}
              key={category}
            >
              <NodeSelectDrawerCategory
                category={category}
                blocks={blocks}
                onSelect={handleSelect}
              />
            </StyledAccordion>
          ))}
        </DrawerStack>
      </ScrollArea>
    </DrawerRoot>
  );
};

type NodeSelectDrawerHeaderProps = {
  query: string;
  onQueryChange: (value: string) => void;
};

const NodeSelectDrawerHeader = forwardRef<
  HTMLInputElement,
  NodeSelectDrawerHeaderProps
>(({ query, onQueryChange }, ref) => {
  /** Context state */
  const closeDrawer = useGraphContext(({ ui }) => ui.closeDrawer);

  return (
    <Stack spacing="xs" p="sm">
      <Group position="apart">
        <Text size="sm" fw={400}>
          {t('graphEditor.drawer.addNode')}
        </Text>
        <ActionIcon
          variant="subtle"
          radius="md"
          size="sm"
          onClick={() => closeDrawer()}
          aria-label={t('graphEditor.drawer.closeNodeDrawer')}
        >
          <IoCloseSharp size={14} />
        </ActionIcon>
      </Group>
      <TextInput
        ref={ref}
        placeholder={t('common.search')}
        icon={<BsSearch size={14} />}
        size="sm"
        value={query}
        onChange={(event) => onQueryChange(event.currentTarget.value)}
      />
    </Stack>
  );
});

type NodeSelectDrawerCategoryProps = {
  category: keyof typeof LOGIC_CATEGORIES;
  blocks: LogicBlockView[];
  onSelect: (blockType: LogicBlockIndex) => void;
};

const NodeSelectDrawerCategory = ({
  category,
  blocks,
  onSelect,
}: NodeSelectDrawerCategoryProps) => {
  return (
    <Accordion.Item value={category}>
      <Accordion.Control>
        <Text fz="xs" sx={(theme) => ({ color: theme.colors.gray[7] })}>
          {t(LOGIC_CATEGORIES[category])}
        </Text>
      </Accordion.Control>
      <Accordion.Panel>
        <StyledList>
          {blocks.map((block) => (
            <NodeSelectDrawerItem
              key={block.type}
              block={block}
              onSelect={onSelect}
            />
          ))}
        </StyledList>
      </Accordion.Panel>
    </Accordion.Item>
  );
};

interface NodeSelectDrawerItemProps {
  block: LogicBlockView;
  onSelect: (blockType: LogicBlockIndex) => void;
}

const NodeSelectDrawerItem = ({
  block,
  onSelect,
}: NodeSelectDrawerItemProps) => {
  const { title, description, icon: Icon, type } = block;

  return (
    <List.Item>
      <StyledComponent onClick={() => onSelect(type)}>
        <StyledThemeIcon size="xl" className="node-icon">
          <Icon />
        </StyledThemeIcon>
        <Stack spacing={5}>
          <Title order={6}>{t(title)}</Title>
          <Text size="xs" c="dimmed">
            {t(description)}
          </Text>
        </Stack>
      </StyledComponent>
    </List.Item>
  );
};
